import React, { useState, useEffect } from "react";
import { 
  Utensils, 
  Flame, 
  Droplets, 
  Brain, 
  Sparkles, 
  Activity, 
  Clock, 
  CheckCircle2, 
  Footprints,
  Info,
  Scale,
  ShieldCheck,
  Check,
  Dumbbell,
  Timer,
  HeartPulse,
  ChevronRight,
  AlertTriangle,
  Zap,
  ArrowRight,
  RefreshCw,
  Radio,
  Lock,
  MessageSquare,
  ExternalLink
} from "lucide-react";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { 
  getDailyDashboard, 
  calculateRemainingCalories,
  DEMO_DAILY_DASHBOARD,
  getWhatsAppChatUrl
} from "../../services/coreServicesClient";

/**
 * Donut / Circular Gauge para Balance Nutricional (Tema Claro WE360)
 */
function CalorieDonutChart({ consumed = 0, target = 2000, size = 128, strokeWidth = 12 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percent = target > 0 ? Math.min(100, Math.max(0, (consumed / target) * 100)) : 0;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div 
      className="relative flex items-center justify-center shrink-0" 
      style={{ width: size, height: size }}
      data-testid="calorie-donut-chart"
    >
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Pista de fondo clara */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E2E8F0"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progreso en verde esmeralda */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#10B981"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="transparent"
          className="transition-all duration-700 ease-out"
        />
      </svg>
      {/* Texto Central */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Consumo</span>
        <span className="text-xl font-black text-slate-900 tracking-tight" data-testid="donut-consumed-calories">
          {consumed}
        </span>
        <span className="text-[9px] text-slate-500 font-semibold">kcal</span>
      </div>
    </div>
  );
}

/**
 * Jarra / Termo Volumétrico para Hidratación Celular (Tema Claro WE360)
 */
function VolumetricThermos({ consumedMl = 0, targetMl = 3310, className = "" }) {
  const percent = targetMl > 0 ? Math.min(100, Math.max(0, Math.round((consumedMl / targetMl) * 100))) : 0;
  const consumedLiters = (consumedMl / 1000).toFixed(1);
  const targetLiters = (targetMl / 1000).toFixed(1);
  const midLiters = ((targetMl / 2) / 1000).toFixed(1);

  return (
    <div 
      className={`relative w-20 h-32 bg-sky-50/70 border-2 border-sky-200/90 rounded-2xl p-1 flex flex-col justify-end overflow-hidden shadow-inner shrink-0 ${className}`} 
      data-testid="hydration-thermos"
    >
      {/* Marcas de volumen */}
      <div className="absolute top-2 left-2 text-[8px] font-mono text-sky-800 font-bold z-10">{`${targetLiters}L`}</div>
      <div className="absolute top-2 right-1.5 w-1.5 h-0.5 bg-sky-300 z-10" />
      <div className="absolute top-1/2 -translate-y-1/2 left-2 text-[8px] font-mono text-sky-800 font-bold z-10">{`${midLiters}L`}</div>
      <div className="absolute top-1/2 -translate-y-1/2 right-1.5 w-1.5 h-0.5 bg-sky-300 z-10" />
      
      {/* Nivel de Líquido Dinámico */}
      <div 
        className="w-full rounded-xl bg-gradient-to-t from-[#0284C7] to-[#06B6D4] transition-all duration-700 ease-out flex items-center justify-center relative shadow-sm"
        style={{ height: `${Math.max(22, percent)}%` }}
        data-testid="hydration-liquid"
      >
        <span className="text-[11px] font-black text-white drop-shadow-sm tracking-tight">
          {`${consumedLiters} L`}
        </span>
      </div>
    </div>
  );
}

/**
 * WellnessBalancesWidget - WE360
 *
 * Conectado a Core Services (GET /api/user/daily-dashboard).
 * Integra el Semáforo de Parámetros Clínicos de 6 tarjetas,
 * Balance Nutricional (Atwater 100%), Hidratación Celular (Jarra de agua + Vasos)
 * y Actividad Física (Cero Calorías Netas).
 * Adaptado 100% a la paleta clínica y diseño premium de WE360.
 */
export default function WellnessBalancesWidget({
  profile: propProfile,
  isDemo: propIsDemo,
  latestMeasurement,
  onNavigateToMeasurement,
  onNavigateToDiet,
  onNavigateToActivity,
  className = "",
}) {
  let contextValue = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    contextValue = useUserProfile();
  } catch (e) {
    // Fuera de provider seguro
  }

  const profile = propProfile || contextValue?.profile || {};
  const isDemo = propIsDemo !== undefined ? propIsDemo : (contextValue?.isDemo ?? false);
  const userPhone = profile.whatsapp || profile.telefono || profile.phone || "";
  const userEmail = profile.email || "";

  const [dashboardData, setDashboardData] = useState(isDemo ? DEMO_DAILY_DASHBOARD : null);
  const [loading, setLoading] = useState(true);
  const [isLiveApi, setIsLiveApi] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      setLoading(true);
      try {
        // En demo o con usuario conectado, intentamos consultar el endpoint dev en vivo
        const queryPhone = userPhone || (isDemo ? "584124281735" : "");
        const queryDate = isDemo ? "2026-09-30" : new Date().toISOString().split("T")[0];
        const data = await getDailyDashboard(queryPhone, userEmail, queryDate, { isDemo, fetchLive: true });
        if (isMounted) {
          if (data && data.status === "Success") {
            setDashboardData(data);
            setIsLiveApi(Boolean(data?.phone === "584124281735" || userPhone || userEmail));
          } else {
            setDashboardData(isDemo ? DEMO_DAILY_DASHBOARD : null);
          }
        }
      } catch (err) {
        console.warn("[WellnessBalancesWidget] Error fetching daily dashboard:", err);
        if (isMounted && isDemo) {
          setDashboardData(DEMO_DAILY_DASHBOARD);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [userPhone, userEmail, isDemo]);

  // Si usuario es demo o tiene data
  const effectiveData = dashboardData || (isDemo ? DEMO_DAILY_DASHBOARD : null);

  const clinical = effectiveData?.clinicalTrackingParameters || {};
  const lm = latestMeasurement || {};
  const lmFields = lm.fields || lm.vitals || lm;
  const p = profile || {};
  const f1 = p.f1Data || {};

  // 1. Presión Arterial (Nivel Hemodinámico)
  const rawSystolic = lmFields.paSistolica ?? lmFields.sbp ?? p.paSistolica ?? f1.paSistolica ?? (isDemo ? clinical.bloodPressure?.systolic : (clinical.bloodPressure?.systolic ?? null));
  const rawDiastolic = lmFields.paDiastolica ?? lmFields.dbp ?? p.paDiastolica ?? f1.paDiastolica ?? (isDemo ? clinical.bloodPressure?.diastolic : (clinical.bloodPressure?.diastolic ?? null));
  const systolic = rawSystolic ? parseFloat(rawSystolic) : null;
  const diastolic = rawDiastolic ? parseFloat(rawDiastolic) : null;
  const hasBp = Boolean(systolic && diastolic);

  let bpStatus = "PENDIENTE";
  let bpBadgeClass = "bg-slate-100 text-slate-600 border-slate-200";
  let bpLabel = "Sin registro de presión arterial hoy. Se recomienda registrar una toma matutina o realizar un escaneo.";

  if (isDemo && clinical.bloodPressure?.status) {
    bpStatus = clinical.bloodPressure.status === "OPTIMAL" ? "ÓPTIMO" : "RIESGO";
    bpBadgeClass = bpStatus === "ÓPTIMO" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
    bpLabel = clinical.bloodPressure.label || "Condición Hipertensiva Diagnosticada (Sin lectura hoy). Recomendado registrar toma matutina.";
  } else if (hasBp) {
    if (systolic < 120 && diastolic < 80) {
      bpStatus = "ÓPTIMO";
      bpBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
      bpLabel = "Nivel hemodinámico óptimo según guías clínicas (<120/80 mmHg).";
    } else if (systolic >= 140 || diastolic >= 90) {
      bpStatus = "RIESGO";
      bpBadgeClass = "bg-rose-50 text-rose-700 border-rose-200";
      bpLabel = "Lectura de presión arterial elevada. Mantenga seguimiento con su médico.";
    } else {
      bpStatus = "MODERADO";
      bpBadgeClass = "bg-amber-50 text-amber-700 border-amber-200";
      bpLabel = "Presión arterial en rango limítrofe.";
    }
  }

  // 2. Composición Corporal y Peso (Antropometría)
  const rawWeight = lmFields.peso ?? lmFields.weight ?? p.peso ?? p.weight ?? f1.peso ?? (isDemo ? clinical.bodyComposition?.weightKg : (clinical.bodyComposition?.weightKg ?? null));
  const rawHeight = lmFields.talla ?? lmFields.estatura ?? lmFields.altura ?? lmFields.height ?? p.talla ?? p.estatura ?? p.altura ?? p.height ?? f1.talla ?? f1.estatura ?? (isDemo ? clinical.bodyComposition?.heightCm : (clinical.bodyComposition?.heightCm ?? null));
  const weightKg = rawWeight ? parseFloat(rawWeight) : null;
  const heightCm = rawHeight ? parseFloat(rawHeight) : null;

  let bmi = lmFields.cardiovascularBMI ?? p.imc ?? p.bmi ?? f1.imc ?? (isDemo ? clinical.bodyComposition?.bmi : (clinical.bodyComposition?.bmi ?? null));
  if (bmi) {
    bmi = parseFloat(bmi);
  } else if (weightKg && heightCm && heightCm > 0) {
    bmi = parseFloat((weightKg / Math.pow(heightCm / 100, 2)).toFixed(1));
  }

  const hasBodyComp = Boolean(weightKg || heightCm || bmi);
  let bodyCompClass = "PENDIENTE";
  let bodyCompBadgeClass = "bg-slate-100 text-slate-600 border-slate-200";

  if (isDemo && clinical.bodyComposition?.classification) {
    bodyCompClass = clinical.bodyComposition.classification === "OVERWEIGHT" ? "SOBREPESO" : clinical.bodyComposition.classification;
    bodyCompBadgeClass = "bg-amber-50 text-amber-700 border-amber-200";
  } else if (bmi) {
    if (bmi < 18.5) {
      bodyCompClass = "BAJO PESO";
      bodyCompBadgeClass = "bg-amber-50 text-amber-700 border-amber-200";
    } else if (bmi < 25) {
      bodyCompClass = "NORMAL";
      bodyCompBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (bmi < 30) {
      bodyCompClass = "SOBREPESO";
      bodyCompBadgeClass = "bg-amber-50 text-amber-700 border-amber-200";
    } else {
      bodyCompClass = "OBESIDAD";
      bodyCompBadgeClass = "bg-rose-50 text-rose-700 border-rose-200";
    }
  }

  // 3. Control Glucémico (Metabolismo de Glucosa)
  const rawGlucose = lmFields.glucemia ?? p.glucemia ?? f1.glucemia ?? clinical.glycemicControl?.glucoseMgDl ?? null;
  const glucoseMgDl = rawGlucose ? parseFloat(rawGlucose) : null;
  const hasGlucose = Boolean(glucoseMgDl !== null && glucoseMgDl !== undefined && !Number.isNaN(glucoseMgDl));

  let glucoseStatus = "PENDIENTE";
  let glucoseBadgeClass = "bg-slate-100 text-slate-600 border-slate-200";
  let glucoseLabel = "Sin registro glucémico hoy. Puede registrarlo en Nueva Medición o laboratorio.";

  if (isDemo) {
    glucoseStatus = clinical.glycemicControl?.status === "OPTIMAL" ? "ÓPTIMO" : "EN RANGO";
    glucoseBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    glucoseLabel = clinical.glycemicControl?.label || "Sensibilidad a la insulina preservada y sin picos de hiperglucemia reportados.";
  } else if (hasGlucose) {
    if (glucoseMgDl < 100) {
      glucoseStatus = "ÓPTIMO";
      glucoseBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
      glucoseLabel = "Nivel de glucosa basal normoglucémico (< 100 mg/dL).";
    } else if (glucoseMgDl < 126) {
      glucoseStatus = "ALERTA";
      glucoseBadgeClass = "bg-amber-50 text-amber-700 border-amber-200";
      glucoseLabel = "Glucemia basal alterada en ayuno (100 - 125 mg/dL).";
    } else {
      glucoseStatus = "ELEVADO";
      glucoseBadgeClass = "bg-rose-50 text-rose-700 border-rose-200";
      glucoseLabel = "Glucemia basal elevada (≥ 126 mg/dL). Consultar con su médico.";
    }
  }

  // 4. Estrés Emocional y SNA (VFC / Tono Vagal)
  const rawHrv = lmFields.heartRateVariability ?? lmFields.hrv ?? p.heartRateVariability ?? (isDemo ? (clinical.emotionalStress?.hrvMs || clinical.emotionalStress?.basalHrv || 45) : (clinical.emotionalStress?.hrvMs || clinical.emotionalStress?.basalHrv || null));
  const hrvMs = rawHrv ? parseFloat(rawHrv) : null;
  const rawStress = lmFields.stressIndex ?? p.stressIndex ?? (isDemo ? 120 : (clinical.emotionalStress?.stressIndex ?? null));
  const stressIndex = rawStress !== null && rawStress !== undefined ? parseFloat(rawStress) : null;
  const hasStress = Boolean(hrvMs !== null || stressIndex !== null);

  let stressLevel = "PENDIENTE";
  let stressStatus = "PENDIENTE";
  let stressBadgeClass = "bg-slate-100 text-slate-600 border-slate-200";
  let stressLabel = "Sin escaneo de estrés o VFC hoy. Realice un escaneo facial de 30 segundos.";

  if (isDemo) {
    stressLevel = clinical.emotionalStress?.stressLevel || "BAJO";
    stressStatus = clinical.emotionalStress?.status === "OPTIMAL" ? "ÓPTIMO" : "CONTROLADO";
    stressBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    stressLabel = clinical.emotionalStress?.label || "Tono Parasimpático Favorable / Recuperación Óptima del sistema nervioso autónomo.";
  } else if (hasStress) {
    if (stressIndex !== null) {
      stressLevel = stressIndex < 150 ? "BAJO" : (stressIndex < 400 ? "MODERADO" : "ELEVADO");
      stressStatus = stressIndex < 250 ? "ÓPTIMO" : (stressIndex < 400 ? "MODERADO" : "ALERTA");
    } else {
      stressLevel = hrvMs >= 40 ? "BAJO" : "MODERADO";
      stressStatus = hrvMs >= 35 ? "ÓPTIMO" : "MODERADO";
    }
    stressBadgeClass = stressStatus === "ÓPTIMO" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
    stressLabel = "Tono Parasimpático y recuperación del sistema nervioso autónomo evaluados.";
  }

  // 5. Inactividad Física y Movimiento
  const rawSteps = isDemo ? (clinical.physicalInactivity?.steps ?? effectiveData?.activity?.steps ?? 5500) : (clinical.physicalInactivity?.steps ?? effectiveData?.activity?.steps ?? 0);
  const steps = rawSteps ? parseInt(rawSteps, 10) : 0;
  const hasSteps = steps > 0;

  let movementStatus = "0% META";
  let movementBadgeClass = "bg-slate-100 text-slate-600 border-slate-200";
  let movementLabel = "Sin actividad física sincronizada hoy. Conecte su tracker o registre sus ejercicios.";

  if (isDemo) {
    movementStatus = steps >= 10000 ? "ÓPTIMO" : (steps >= 5000 ? "MODERADO" : "RIESGO");
    movementBadgeClass = steps >= 10000 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
    movementLabel = clinical.physicalInactivity?.label || "Actividad Moderada / Sedentarismo Leve (3,000 - 6,999 pasos). Sugerida caminata ligera post-cena.";
  } else if (hasSteps) {
    movementStatus = steps >= 10000 ? "ÓPTIMO" : (steps >= 5000 ? "MODERADO" : "RIESGO");
    movementBadgeClass = steps >= 10000 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
    movementLabel = steps >= 10000 ? "Meta de movimiento cumplida. Excelente volumen activo diario." : "Actividad en progreso. Camine 10-15 minutos adicionales para alcanzar su meta.";
  }

  // 6. Perfil Lipídico (Fracción c-LDL)
  const rawLdl = lmFields.ldl ?? p.ldl ?? f1.ldl ?? (isDemo ? (clinical.lipidProfile?.ldlMgDl ?? 88) : (clinical.lipidProfile?.ldlMgDl ?? null));
  const ldlMgDl = rawLdl ? parseFloat(rawLdl) : null;
  const hasLdl = Boolean(ldlMgDl !== null && ldlMgDl !== undefined && !Number.isNaN(ldlMgDl));

  let ldlStatus = "PENDIENTE";
  let ldlBadgeClass = "bg-slate-100 text-slate-600 border-slate-200";
  let ldlLabel = "Sin perfil lipídico reciente. Ingrese sus resultados de laboratorio para monitorear aterogenicidad.";

  if (isDemo) {
    ldlStatus = clinical.lipidProfile?.status === "OPTIMAL" ? "ÓPTIMO" : "CONTROLADO";
    ldlBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    ldlLabel = clinical.lipidProfile?.label || "Perfil Lipídico Controlado. Aterogenicidad y riesgo cardiovascular aterosclerótico bajo.";
  } else if (hasLdl) {
    if (ldlMgDl < 100) {
      ldlStatus = "ÓPTIMO";
      ldlBadgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
      ldlLabel = "Fracción c-LDL óptima (< 100 mg/dL). Riesgo aterosclerótico bajo.";
    } else if (ldlMgDl < 130) {
      ldlStatus = "MODERADO";
      ldlBadgeClass = "bg-amber-50 text-amber-700 border-amber-200";
      ldlLabel = "c-LDL en rango limítrofe (100 - 129 mg/dL).";
    } else {
      ldlStatus = "RIESGO";
      ldlBadgeClass = "bg-rose-50 text-rose-700 border-rose-200";
      ldlLabel = "c-LDL elevado (≥ 130 mg/dL). Monitorear dieta y consultar a su médico.";
    }
  }

  // Verificación sincera de existencia de telemetría clínica
  const hasClinicalData = isDemo || Boolean(
    hasBp || hasBodyComp || hasGlucose || hasStress || hasSteps || hasLdl
  );

  const nutrition = effectiveData?.nutrition || {
    targetCalories: 2134,
    consumedCalories: 0,
    remainingCalories: 2134,
    macros: {
      carbs: { targetGrams: 267, consumedGrams: 0 },
      protein: { targetGrams: 107, consumedGrams: 0 },
      fat: { targetGrams: 71, consumedGrams: 0 }
    },
    meals: []
  };

  const hydration = effectiveData?.hydration || {
    targetMl: weightKg ? Math.round(weightKg * 35) : 3310,
    consumedMl: 0,
    remainingMl: weightKg ? Math.round(weightKg * 35) : 3310
  };

  const activity = effectiveData?.activity || {
    steps: 0,
    activeCaloriesBurned: 0,
    bonusMetabolico: 0,
    exercises: []
  };

  // Porcentaje calórico
  const calPercent = Math.min(100, Math.round(((nutrition.consumedCalories || 0) / (nutrition.targetCalories || 1)) * 100));
  // Porcentaje hídrico
  const hydroPercent = Math.min(100, Math.round(((hydration.consumedMl || 0) / (hydration.targetMl || 1)) * 100));

  // Macros porcentajes
  const carbsG = nutrition.macros?.carbs?.consumedGrams || 0;
  const carbsTarget = nutrition.macros?.carbs?.targetGrams || 267;
  const carbsPercent = Math.min(100, Math.round((carbsG / (carbsTarget || 1)) * 100));

  const proteinG = nutrition.macros?.protein?.consumedGrams || 0;
  const proteinTarget = nutrition.macros?.protein?.targetGrams || 107;
  const proteinPercent = Math.min(100, Math.round((proteinG / (proteinTarget || 1)) * 100));

  const fatG = nutrition.macros?.fat?.consumedGrams || 0;
  const fatTarget = nutrition.macros?.fat?.targetGrams || 71;
  const fatPercent = Math.min(100, Math.round((fatG / (fatTarget || 1)) * 100));

  // Vasos de 250 ml
  const totalCups = Math.max(14, Math.ceil((hydration.targetMl || 3310) / 250));
  const consumedCups = Math.min(totalCups, Math.floor((hydration.consumedMl || 0) / 250));

  // Cálculo seguro del presupuesto restante (Regla Cero Calorías Netas)
  const safeRemaining = calculateRemainingCalories(nutrition.targetCalories, nutrition.consumedCalories);

  // Formato de hora amigable
  const formatMealTime = (isoString) => {
    if (!isoString) return "";
    try {
      const d = new Date(isoString);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "";
    }
  };

  return (
    <div
      className={`bg-white/80 backdrop-blur-md text-slate-900 border border-slate-200/80 rounded-3xl p-5 sm:p-6 shadow-sm transition-all ${className}`}
      data-testid="wellness-balances-widget"
    >
      {/* ── Header Principal ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 border-b border-slate-200/80">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#00628F] bg-[#00628F]/10 border border-[#00628F]/20 px-2.5 py-0.5 rounded-md">
              BioMetrix Portal • CLINICAL 360°
            </span>
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-md border border-emerald-200 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {isLiveApi ? "Sincronizado en Vivo (Core Services Dev API)" : "Sincronizado con Core Services"}
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-1.5 tracking-tight">
            Balances Diarios de Bienestar &amp; Semáforo Clínico
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Integración de telemetría de salud, nutrición según Atwater, hidratación celular y gasto activo.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
          <span className="text-xs font-semibold text-slate-600 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200 flex items-center gap-1.5">
            <Clock size={13} className="text-slate-400" /> {`Hoy (${effectiveData?.date || (isDemo ? "2026-09-30" : new Date().toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }))})`}
          </span>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── BLOQUE 1: SEMÁFORO DE PARÁMETROS CLÍNICOS (6 TARJETAS) ─────── */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div className="mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700">
              <HeartPulse size={17} />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-extrabold text-slate-900 flex items-center gap-2">
                Semáforo de Parámetros Clínicos
              </h3>
              <p className="text-[11px] text-slate-500">
                Biomarcadores sincronizados según última telemetría clínica
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-[11px] font-semibold self-start sm:self-auto">
            <span className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Óptimo
            </span>
            <span className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> Riesgo / Alerta
            </span>
          </div>
        </div>

        {/* Si usuario real sin registros (Sinceridad de Datos) */}
        {!hasClinicalData && !isDemo ? (
          <div 
            className="p-6 rounded-2xl bg-slate-50/70 border border-slate-200/80 text-center my-4"
            data-testid="clinical-parameters-empty-state"
          >
            <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center mx-auto mb-3">
              <Activity size={24} />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Sin telemetría clínica sincronizada</h4>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
              Realice un escaneo facial de 30 segundos o conecte su dispositivo para activar los 6 biomarcadores del semáforo clínico.
            </p>
            {onNavigateToMeasurement && (
              <button
                onClick={onNavigateToMeasurement}
                className="mt-4 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#00628F] hover:bg-[#004e72] transition-all inline-flex items-center gap-1.5 shadow-sm"
              >
                <span>Realizar Escaneo Clínico</span>
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        ) : (
          /* Grid de 6 Tarjetas Clínicas */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clinical-parameters-grid">
            
            {/* 1. Presión Arterial */}
            <div 
              className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
              data-testid="clinical-card-blood-pressure"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center">
                      <HeartPulse size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {clinical.bloodPressure?.name || "Presión Arterial"}
                      </h4>
                      <p className="text-[10px] text-slate-400">Nivel Hemodinámico</p>
                    </div>
                  </div>
                  <span 
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${bpBadgeClass}`}
                  >
                    {bpStatus}
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 my-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] text-slate-500 font-medium">Lectura del día</span>
                    <span className={`text-xs font-bold ${hasBp ? (bpStatus === "ÓPTIMO" ? "text-emerald-700" : "text-amber-700") : (isDemo ? "text-amber-700" : "text-slate-500")}`}>
                      {hasBp ? `${systolic}/${diastolic} mmHg` : "Sin toma hoy"}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-600 mt-1.5 leading-snug">
                    {bpLabel}
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>Objetivo: &lt;120/80 mmHg</span>
                <span className={`font-semibold flex items-center gap-1 ${hasBp ? (bpStatus === "ÓPTIMO" ? "text-emerald-700" : "text-amber-700") : (isDemo ? "text-amber-700" : "text-slate-400")}`}>
                  <Clock size={10} /> {hasBp ? (bpStatus === "ÓPTIMO" ? "Controlado" : "Seguimiento") : "Pendiente"}
                </span>
              </div>
            </div>

            {/* 2. Composición y Peso */}
            <div 
              className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
              data-testid="clinical-card-body-composition"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center">
                      <Scale size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {clinical.bodyComposition?.name || "Composición Corporal y Peso"}
                      </h4>
                      <p className="text-[10px] text-slate-400">Antropometría</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${bodyCompBadgeClass}`}>
                    {bodyCompClass}
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 my-2">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-500">Peso</span>
                      <p className="text-xs font-black text-slate-900">
                        {weightKg ?? "--"} <span className="text-[9px] font-normal text-slate-500">kg</span>
                      </p>
                    </div>
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-500">Talla</span>
                      <p className="text-xs font-black text-slate-900">
                        {heightCm ?? "--"} <span className="text-[9px] font-normal text-slate-500">cm</span>
                      </p>
                    </div>
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-500">IMC</span>
                      <p className={`text-xs font-black ${bmi && bmi >= 18.5 && bmi < 25 ? "text-emerald-700" : (bmi ? "text-amber-700" : "text-slate-500")}`}>
                        {bmi ?? "--"}
                      </p>
                    </div>
                  </div>

                  {/* Barra de Clasificación Tri-Color */}
                  <div className="mt-2.5 pt-2 border-t border-slate-200/60">
                    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-slate-200">
                      <div className="w-1/3 bg-[#06B6D4]" title="Normal (<25)" />
                      <div className="w-1/3 bg-[#F59E0B] ring-1 ring-white" title="Sobrepeso (25 - 29.9)" />
                      <div className="w-1/3 bg-[#F43F5E]" title="Obesidad (>30)" />
                    </div>
                    <div className="flex justify-between text-[8px] text-slate-500 mt-1 font-medium">
                      <span>Normal (&lt;25)</span>
                      <span className="text-amber-700 font-bold">Sobrepeso (25-29.9)</span>
                      <span>Obesidad (&gt;30)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>Meta IMC: &lt; 24.9</span>
                <span>WHtR: {clinical.bodyComposition?.whtr || (hasBodyComp ? "Calculado" : "No registrado")}</span>
              </div>
            </div>

            {/* 3. Control Glucémico */}
            <div 
              className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
              data-testid="clinical-card-glycemic-control"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center">
                      <Droplets size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {clinical.glycemicControl?.name || "Control Glucémico"}
                      </h4>
                      <p className="text-[10px] text-slate-400">Metabolismo de Glucosa</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${glucoseBadgeClass}`}>
                    {glucoseStatus}
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 my-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] text-slate-500 font-medium">{hasGlucose ? "Lectura basal" : "Rango estimado"}</span>
                    <span className={`text-xs font-bold ${hasGlucose ? (glucoseStatus === "ÓPTIMO" ? "text-emerald-700" : "text-amber-700") : (isDemo ? "text-emerald-700" : "text-slate-500")}`}>
                      {hasGlucose ? `${glucoseMgDl} mg/dL` : (isDemo ? "70 – 99 mg/dL" : "--")}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-600 mt-1.5 leading-snug">
                    {glucoseLabel}
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>HbA1c Objetivo: &lt;5.7%</span>
                <span className={`font-semibold flex items-center gap-1 ${hasGlucose || isDemo ? "text-emerald-700" : "text-slate-400"}`}>
                  <CheckCircle2 size={11} /> {hasGlucose || isDemo ? "En Rango" : "Pendiente"}
                </span>
              </div>
            </div>

            {/* 4. Estrés Emocional y SNA */}
            <div 
              className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
              data-testid="clinical-card-emotional-stress"
            >
              <div data-testid="balance-card-emotional">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                      <Brain size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {clinical.emotionalStress?.name || "Estrés Emocional y SNA"}
                      </h4>
                      <p className="text-[10px] text-slate-400">VFC / Tono Vagal</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${stressBadgeClass}`}>
                    {stressStatus}
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 my-2">
                  <div className="flex justify-between items-baseline mb-1">
                    <div>
                      <span className="text-[9px] uppercase font-bold text-slate-500">Nivel Estrés</span>
                      <p className={`text-xs font-bold ${hasStress || isDemo ? "text-emerald-700" : "text-slate-500"}`}>
                        {stressLevel}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-[9px] uppercase font-bold text-slate-500">HRV Basal</span>
                      <p className="text-xs font-black text-slate-900">
                        {hrvMs ? `${hrvMs} ms` : (isDemo ? "45 ms" : "--")}
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-600 mt-1 leading-snug">
                    {stressLabel}
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>Equilibrio Simpático/Vagal</span>
                <span className={`font-semibold ${hasStress || isDemo ? "text-emerald-700" : "text-slate-400"}`}>
                  {hasStress || isDemo ? "Recuperación Lista" : "Pendiente"}
                </span>
              </div>
            </div>

            {/* 5. Movimiento Diario */}
            <div 
              className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
              data-testid="clinical-card-daily-movement"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center">
                      <Footprints size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {clinical.physicalInactivity?.name || "Inactividad Física y Movimiento"}
                      </h4>
                      <p className="text-[10px] text-slate-400">Inactividad Física</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${movementBadgeClass}`}>
                    {movementStatus}
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 my-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-bold text-slate-900">
                      {`${steps.toLocaleString()} pasos completados`}
                    </span>
                    <span className={`text-xs font-extrabold ${steps >= 10000 ? "text-emerald-700" : (steps > 0 ? "text-amber-700" : "text-slate-400")}`}>
                      {`${Math.min(100, Math.round((steps / 10000) * 100))}% meta`}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1.5 overflow-hidden">
                    <div 
                      className={`h-1.5 rounded-full ${steps >= 10000 ? "bg-emerald-500" : "bg-amber-500"}`}
                      style={{ width: `${Math.min(100, Math.round((steps / 10000) * 100))}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-600 mt-1.5 leading-snug">
                    {movementLabel}
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>Meta Clínica: 10,000 pasos</span>
                <span className={steps >= 10000 ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                  {steps >= 10000 ? "Meta Alcanzada" : `+${(10000 - steps).toLocaleString()} para el objetivo`}
                </span>
              </div>
            </div>

            {/* 6. Perfil Lipídico */}
            <div 
              className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between"
              data-testid="clinical-card-lipid-profile"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center">
                      <ShieldCheck size={15} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        {clinical.lipidProfile?.name || "Perfil Lipídico"}
                      </h4>
                      <p className="text-[10px] text-slate-400">Fracción c-LDL</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${ldlBadgeClass}`}>
                    {ldlStatus}
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 my-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] text-slate-500 font-medium">c-LDL Estimado</span>
                    <span className={`text-xs font-bold ${hasLdl || isDemo ? "text-emerald-700" : "text-slate-500"}`}>
                      {hasLdl ? `${ldlMgDl} mg/dL` : (isDemo ? "< 100 mg/dL" : "--")}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-600 mt-1.5 leading-snug">
                    {ldlLabel}
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>Rango cardiovascular: &lt;100 mg/dL</span>
                <span className={`font-semibold flex items-center gap-1 ${hasLdl || isDemo ? "text-emerald-700" : "text-slate-400"}`}>
                  <CheckCircle2 size={11} /> {hasLdl || isDemo ? "Protegido" : "Pendiente"}
                </span>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* ── BLOQUE 2: NUTRICIÓN, HIDRATACIÓN & ACTIVIDAD (2 COLUMNAS) ───── */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <div className="relative mt-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        
        {/* ── COLUMNA IZQUIERDA (7/12): BALANCE NUTRICIONAL & COMIDAS ──── */}
        <div 
          className="lg:col-span-7 p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex flex-col justify-between"
          data-testid="balance-card-nutritional"
        >
          <div data-testid="balance-nutritional-section">
            {/* Header Nutrición */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center">
                  <Utensils size={16} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-slate-900">Balance Nutricional &amp; Calorías</h3>
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      Atwater 100%
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">Ingesta energética y macronutrientes del día</p>
                </div>
              </div>
              <span className="text-[11px] font-bold text-slate-600 bg-slate-50 border border-slate-200 px-2.5 py-0.5 rounded-full">
                {calPercent.toFixed(1)}% de la Meta
              </span>
            </div>

            {/* Donut Chart & Desglose Calórico */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60 flex flex-col sm:flex-row items-center gap-5">
              <CalorieDonutChart 
                consumed={nutrition.consumedCalories} 
                target={nutrition.targetCalories} 
              />
              <div className="flex-1 w-full space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-600 font-medium">Calorías Consumidas</span>
                  </div>
                  <span className="font-extrabold text-slate-900 text-sm" data-testid="consumed-calories">
                    {`${nutrition.consumedCalories} kcal`}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    <span className="text-slate-600 font-medium">Calorías Restantes</span>
                  </div>
                  <span className="font-extrabold text-amber-700 text-sm" data-testid="remaining-calories">
                    {`${safeRemaining} kcal`}
                  </span>
                </div>

                <div className="pt-2 border-t border-slate-200/70 flex items-center justify-between text-[11px] text-slate-500">
                  <span>Objetivo Diario (TDEE Prescrito)</span>
                  <span className="font-bold text-slate-700">{`${nutrition.targetCalories} kcal`}</span>
                </div>
              </div>
            </div>

            {/* Distribución de Macronutrientes */}
            <div className="mt-5">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                Distribución de Macronutrientes
              </h4>
              <div className="space-y-2.5 text-xs">
                {/* Carbohidratos */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                      <span className="w-2 h-2 rounded-full bg-[#06B6D4]" /> Carbohidratos
                    </span>
                    <span className="text-[11px] font-bold text-slate-700">
                      {`${carbsG} g / ${carbsTarget} g (${carbsPercent}%)`}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-[#06B6D4] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${carbsPercent}%` }}
                    />
                  </div>
                </div>

                {/* Proteína */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                      <span className="w-2 h-2 rounded-full bg-[#F43F5E]" /> Proteína
                    </span>
                    <span className="text-[11px] font-bold text-slate-700">
                      {`${proteinG} g / ${proteinTarget} g (${proteinPercent}%)`}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-[#F43F5E] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${proteinPercent}%` }}
                    />
                  </div>
                </div>

                {/* Grasas Saludables */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                      <span className="w-2 h-2 rounded-full bg-[#F59E0B]" /> Grasas Saludables
                    </span>
                    <span className="text-[11px] font-bold text-slate-700">
                      {`${fatG} g / ${fatTarget} g (${fatPercent}%)`}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-[#F59E0B] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${fatPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Comidas Registradas */}
            <div className="mt-5 pt-4 border-t border-slate-200/80">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                  <Utensils size={13} className="text-emerald-600" />
                  Comidas Registradas ({nutrition.meals?.length || 0})
                </h4>
                <span className="text-[10px] text-slate-500">Sincronizado vía bot</span>
              </div>

              {nutrition.meals?.length > 0 ? (
                <div className="space-y-2.5" data-testid="meals-list">
                  {nutrition.meals.map((meal, idx) => {
                    const isBreakfast = meal.category?.toLowerCase().includes("desayuno");
                    const isLunch = meal.category?.toLowerCase().includes("almuerzo");
                    const badgeClass = isBreakfast 
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : (isLunch ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-sky-50 text-sky-800 border-sky-200");

                    return (
                      <div 
                        key={idx} 
                        className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 flex flex-col justify-between hover:bg-white hover:shadow-xs transition-all"
                        data-testid={`meal-item-${idx}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${badgeClass}`}>
                                {meal.category || "Comida"}
                              </span>
                              <span className="text-[10px] text-slate-500">
                                {formatMealTime(meal.loggedAt) || "Hoy"}
                              </span>
                            </div>
                            <h5 className="text-xs font-bold text-slate-900">{meal.name}</h5>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              {meal.portion_reason || "Porción individual completa"} • {meal.alcohol_g || 0}g Alcohol • {meal.caffeine_shots || 0} Shots Cafeína
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-sm font-black text-slate-900">{meal.calories}</span>
                            <span className="text-[10px] text-slate-500 block font-medium">kcal</span>
                          </div>
                        </div>

                        {/* Macros Tags */}
                        <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-200/60 text-[10px]">
                          <span className="text-[#0284C7] font-semibold">
                            Carbs: {meal.carbs_g || 0}g
                          </span>
                          <span className="text-slate-300">•</span>
                          <span className="text-[#E11D48] font-semibold">
                            Proteína: {meal.protein_g || 0}g
                          </span>
                          <span className="text-slate-300">•</span>
                          <span className="text-[#D97706] font-semibold">
                            Grasas: {meal.fat_g || 0}g
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 text-center text-xs text-slate-500">
                  No hay comidas registradas hoy. Escriba al bot de WhatsApp para registrar su plato.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── COLUMNA DERECHA (5/12): HIDRATACIÓN CELULAR & ACTIVIDAD ──── */}
        <div className="lg:col-span-5 flex flex-col gap-5">
          
          {/* 1. Hidratación Celular */}
          <div 
            className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex flex-col justify-between"
            data-testid="balance-card-hydration"
          >
            <div>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-sky-50 border border-sky-200 text-sky-700 flex items-center justify-center">
                    <Droplets size={16} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900">Hidratación Celular</h3>
                      <span className="text-[10px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full">
                        ~35 ml / kg
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">Control hídrico diario ajustado a peso</p>
                  </div>
                </div>
                <span className="text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2.5 py-0.5 rounded-full">
                  {hydroPercent.toFixed(1)}%
                </span>
              </div>

              {/* Jarra y Métricas */}
              <div className="flex items-center gap-4 p-3.5 rounded-xl bg-slate-50 border border-slate-200/60">
                <VolumetricThermos 
                  consumedMl={hydration.consumedMl} 
                  targetMl={hydration.targetMl} 
                />
                <div className="flex-1 space-y-1.5">
                  <div>
                    <span className="text-[10px] text-slate-500 uppercase font-bold">Consumido</span>
                    <p className="text-xl font-black text-[#00628F]" data-testid="consumed-hydration">
                      {hydration.consumedMl?.toLocaleString()} <span className="text-xs font-medium text-slate-500">ml</span>
                    </p>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Faltante para meta:</span>
                    <span className="font-bold text-slate-900">{hydration.remainingMl?.toLocaleString()} ml</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                    <div 
                      className="bg-[#06B6D4] h-1.5 rounded-full"
                      style={{ width: `${hydroPercent}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 leading-tight">
                    Meta recomendada: {hydration.targetMl?.toLocaleString()} ml (aprox. ~{Math.ceil(hydration.remainingMl / 250)} vasos restantes).
                  </p>
                </div>
              </div>

              {/* Fila de VASOS (250 ML C/U) */}
              <div className="mt-4 pt-3 border-t border-slate-200/80">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                  Vasos (250 ml c/u)
                </h4>
                <div className="grid grid-cols-7 gap-1.5" data-testid="glasses-grid">
                  {Array.from({ length: 14 }).map((_, idx) => {
                    const isDrank = idx < consumedCups;
                    return (
                      <div
                        key={idx}
                        className={`h-8 rounded-lg flex items-center justify-center transition-all ${
                          isDrank 
                            ? "bg-[#06B6D4] text-white shadow-xs" 
                            : "bg-slate-100 border border-slate-200 text-slate-400"
                        }`}
                        title={`Vaso #${idx + 1} (250 ml)${isDrank ? " - Consumido" : ""}`}
                        data-testid={`cup-item-${idx}`}
                      >
                        {isDrank ? (
                          <Check size={14} strokeWidth={3} className="text-white" />
                        ) : (
                          <span className="text-xs font-bold leading-none">•</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* 2. Actividad Física & Ejercicio */}
          <div 
            className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex flex-col justify-between"
            data-testid="balance-card-energy"
          >
            <div>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center">
                    <Flame size={16} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-slate-900">Actividad Física &amp; Ejercicio</h3>
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                        Cero Calorías Netas
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">Gasto metabólico activo y sesiones registradas</p>
                  </div>
                </div>
              </div>

              {/* Grid 2 Métricas Principales */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Quemadas Activas */}
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/60">
                  <div className="flex items-center gap-1 text-[10px] font-bold text-amber-700 uppercase">
                    <Zap size={12} /> Quemadas Activas
                  </div>
                  <p className="text-xl font-black text-slate-900 mt-1">
                    {activity.activeCaloriesBurned || activity.bonusMetabolico || 0}{" "}
                    <span className="text-xs font-normal text-slate-500">kcal</span>
                  </p>
                  <p className="text-[9px] text-slate-500 mt-0.5">
                    {`Bonus Metabólico: ${activity.bonusMetabolico || 0} kcal`}
                  </p>
                </div>

                {/* Total Pasos */}
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/60">
                  <div className="flex items-center gap-1 text-[10px] font-bold text-teal-700 uppercase">
                    <Footprints size={12} /> Total Pasos
                  </div>
                  <p className="text-xl font-black text-slate-900 mt-1" data-testid="activity-steps">
                    {(activity.steps || 0).toLocaleString()}
                  </p>
                  <p className="text-[9px] text-slate-500 mt-0.5">
                    {(activity.steps || 0) > 0 ? (isDemo ? "55 min actividad continua" : `${Math.round((activity.steps || 0) / 100)} min actividad activa`) : "0 min actividad registrada"}
                  </p>
                </div>
              </div>

              {/* Entrenamientos Realizados */}
              <div className="pt-3 border-t border-slate-200/80">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                  Entrenamientos Realizados
                </h4>

                {activity.exercises?.length > 0 ? (
                  <div className="space-y-2" data-testid="exercises-list">
                    {activity.exercises.map((item, idx) => {
                      const isCrossfit = item.activityName?.toLowerCase().includes("crossfit");
                      return (
                        <div 
                          key={idx} 
                          className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/70 flex items-center justify-between gap-2 hover:bg-white hover:shadow-xs transition-all"
                          data-testid={`exercise-item-${idx}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                              isCrossfit ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            }`}>
                              {isCrossfit ? <Dumbbell size={14} /> : <Timer size={14} />}
                            </div>
                            <div>
                              <h5 className="text-xs font-bold text-slate-900">
                                {item.activityName || item.activity || "Entrenamiento"}
                              </h5>
                              <p className="text-[10px] text-slate-500">
                                {formatMealTime(item.loggedAt) || "11:00 AM"} • {item.durationMin ? `${item.durationMin} min` : "Alta intensidad"} {item.steps ? `• ${item.steps.toLocaleString()} pasos` : ""}
                              </p>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-xs font-black text-slate-900">
                              {item.caloriesBurned}
                            </span>
                            <span className="text-[9px] text-slate-500 block font-medium">kcal quemadas</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70 text-center text-xs text-slate-500">
                    Sin sesiones de ejercicio registradas hoy.
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Overlay de Bloqueo Progresivo para Usuarios Reales sin Interacción en WhatsApp ── */}
      {!(
        isDemo ||
        (nutrition.consumedCalories || 0) > 0 ||
        (Array.isArray(nutrition.meals) && nutrition.meals.length > 0) ||
        (hydration.consumedMl || 0) > 0 ||
        (activity.steps || 0) > 0 ||
        (activity.activeCaloriesBurned || 0) > 0 ||
        (Array.isArray(activity.exercises) && activity.exercises.length > 0)
      ) && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75 backdrop-blur-md rounded-2xl p-6 text-center border border-slate-200/80 shadow-[inset_0_4px_30px_rgba(0,0,0,0.03)]"
          data-testid="whatsapp-balances-locked-overlay"
        >
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-700 mb-3 shadow-xs">
            <Lock size={22} />
          </div>
          <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 mb-2">
            Sincronización con Agente de WhatsApp
          </span>
          <h4 className="text-sm sm:text-base font-extrabold text-slate-900 mb-1.5">
            Desbloquea tus Balances Diarios de Nutrición, Hidratación y Actividad
          </h4>
          <p className="text-xs text-slate-600 max-w-md leading-relaxed mb-4">
            Estos balances en tiempo real se activan automáticamente en cuanto envías tu primera foto de comida, vasos de agua o actividad al Coach de Bienestar WE360 por WhatsApp.
          </p>
          <a
            href={getWhatsAppChatUrl(undefined, "Hola, quiero registrar mi hábito")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#25D366] hover:bg-[#20ba5a] transition-all shadow-sm"
          >
            <MessageSquare size={14} />
            <span>Activar con Coach en WhatsApp</span>
            <ExternalLink size={12} className="opacity-80" />
          </a>
        </div>
      )}
      </div>
    </div>
  );
}
